import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { withRouter } from '../../common/withRouter';
import { ButtonOBSOLETED, Notification, Message } from '../../web.common';
import PageTop from '../layout/PageTop';
import withApi from '../higherOrderComponents/withApi';

// Tiny html-escape for the preview-window header — the body comes from the
// backend renderer which already encodes user-supplied strings, so we don't
// need a full sanitizer there.
const escapeHtml = (s) => String(s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// Session detail: roster + watchlist on the left, courts grid on the right.
// Drag a roster row onto a court slot to place; drag between slots to swap;
// drag back to the roster to remove. Backend persists each move via PATCH.
const PlayEventSessionInternal = (props) => {
  const sessionId = props.match?.params?.sessionId || props.params?.sessionId;
  const eventId = props.match?.params?.eventId || props.params?.eventId;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await props.api.playEvents.getSession(sessionId);
      setData(d);
    } catch (e) {
      setNotification(new Message('error', `Failed to load: ${e?.message || 'unknown'}`));
    } finally {
      setLoading(false);
    }
  }, [props.api, sessionId]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    setBusy(true);
    try {
      await props.api.playEvents.generatePairings(sessionId, { strategy: 1 /* GenderSegregatedLevelBalanced */ });
      setNotification(new Message('success', 'Pairings generated'));
      await load();
    } catch (e) {
      setNotification(new Message('error', `Generate failed: ${e?.message || 'unknown'}`));
    } finally {
      setBusy(false);
    }
  };

  const handleSend = async () => {
    if (!window.confirm(`Send pairings email to all ${data?.roster?.length ?? '?'} confirmed players right now? Emails go out synchronously.`)) return;
    setBusy(true);
    try {
      const r = await props.api.playEvents.sendPairingsEmail(sessionId);
      setNotification(new Message('success', `Sent ${r.sent} · skipped ${r.skipped} · failed ${r.failed} (attempted ${r.attempted})`));
    } catch (e) {
      setNotification(new Message('error', `Send failed: ${e?.message || 'unknown'}`));
    } finally {
      setBusy(false);
    }
  };

  // Per-recipient email preview. We open the rendered html in a new window so
  // the admin sees exactly what the player will receive — including locale,
  // court assignment, and teammate names — without committing to send.
  const handlePreview = async (registrationId) => {
    try {
      const r = await props.api.playEvents.previewPairing(sessionId, registrationId);
      const w = window.open('', '_blank', 'width=720,height=900');
      if (!w) {
        setNotification(new Message('error', 'Popup blocked — allow popups for this site to preview the email.'));
        return;
      }
      w.document.title = `Preview: ${r.subject} → ${r.toEmail}`;
      w.document.write(`<div style="background:#FAFAFA;border-bottom:1px solid #DDD;padding:10px 14px;font:13px/1.4 -apple-system,sans-serif;color:#333"><b>To:</b> ${escapeHtml(r.toEmail)}<br><b>Subject:</b> ${escapeHtml(r.subject)}</div>`);
      w.document.write(r.html);
      w.document.close();
    } catch (e) {
      setNotification(new Message('error', `Preview failed: ${e?.message || 'unknown'}`));
    }
  };

  if (loading) return <div style={{ padding: 16 }}>Loading…</div>;
  if (!data) return <div style={{ padding: 16 }}>Session not found.</div>;

  return (
    <>
      <Notification newMessage={notification} />
      <PageTop documentTitle='Session detail' documentDescription='Roster + pairings' mainHeadingContent={`${data.eventNameEn} — Session`} />

      <div style={{ padding: 16, color: '#444' }}>
        <div style={{ marginBottom: 8 }}>
          <Link to={`/play/events/${eventId}`} style={{ color: '#666', fontSize: 13 }}>← Back to event</Link>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 12, color: '#666' }}>{data.sport}</div>
            <h2 style={{ margin: '4px 0', color: '#1a3559' }}>
              {new Date(data.startsAtUtc).toLocaleString()} – {new Date(data.endsAtUtc).toLocaleString([], { hour: '2-digit', minute: '2-digit' })}
            </h2>
            <div style={{ color: '#666' }}>{data.roster.length} / {data.capacity} signed up · {data.courtCount} courts</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <ButtonOBSOLETED onClick={handleGenerate} disabled={busy} buttonContent={busy ? '…' : '↻ Generate pairings'} />
            <ButtonOBSOLETED onClick={handleSend} disabled={busy || !data.pairings} buttonContent='Send pairings email' />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 320px) 1fr', gap: 16 }}>
          <div>
            <RosterBlock data={data} api={props.api} onChange={load} onPreview={handlePreview} />
            <WatchlistBlock data={data} />
          </div>
          <div>
            <CourtsBlock data={data} api={props.api} onChange={load} />
            <PairingsInfo data={data} />
          </div>
        </div>
      </div>
    </>
  );
};

// ----------------------------------------------------------------------------
// Roster: list of confirmed players. Draggable to court slots.
// ----------------------------------------------------------------------------
const RosterBlock = ({ data, onPreview }) => {
  const placedRegs = new Set((data.pairings?.placements || []).map((p) => p.registrationId));
  return (
    <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, marginBottom: 12 }}>
      <div style={{ padding: '10px 12px', borderBottom: '1px solid #eee', fontWeight: 600, fontSize: 13 }}>
        Roster · {data.roster.length} / {data.capacity}
      </div>
      <div style={{ maxHeight: 480, overflowY: 'auto' }}>
        {data.roster.map((r) => {
          const placed = placedRegs.has(r.registrationId);
          return (
            <div
              key={r.registrationId}
              draggable
              onDragStart={(e) => e.dataTransfer.setData('regId', r.registrationId)}
              style={{
                padding: '8px 12px',
                borderBottom: '1px solid #f3f3f3',
                cursor: 'grab',
                opacity: placed ? 0.55 : 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>
                  {r.name || r.email}
                  {placed && <span style={{ marginLeft: 6, color: '#999', fontSize: 11, fontWeight: 400 }}>· on court</span>}
                </div>
                {placed && onPreview && (
                  <button
                    type='button'
                    onClick={(e) => { e.stopPropagation(); onPreview(r.registrationId); }}
                    title='Preview the email this player will receive'
                    style={{ border: '1px solid #d0d0d0', background: '#fafafa', color: '#444', fontSize: 11, padding: '2px 8px', borderRadius: 4, cursor: 'pointer' }}
                  >
                    Preview
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: 11 }}>
                <Pill label={r.gender || '—'} color={r.gender === 'Female' ? 'pink' : r.gender === 'Male' ? 'blue' : 'gray'} />
                <Pill label={`L${r.level || '?'}`} color='cream' />
                {r.partnerEmail && <span style={{ color: '#FF6A3D', fontSize: 10 }}>↔ {r.partnerEmail.split('@')[0]}</span>}
              </div>
              {r.levelText && <div style={{ color: '#999', fontSize: 11 }}>{r.levelText}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const WatchlistBlock = ({ data }) => (
  <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8 }}>
    <div style={{ padding: '10px 12px', borderBottom: '1px solid #eee', fontWeight: 600, fontSize: 13 }}>
      Watchlist · {data.watchlist.length}
    </div>
    <div style={{ padding: 10, fontSize: 12, color: '#666' }}>
      When a spot opens, everyone here is notified at the same time — first to click takes it.
    </div>
    {data.watchlist.map((w) => (
      <div key={w.userId} style={{ padding: '6px 12px', borderTop: '1px solid #f3f3f3', fontSize: 13 }}>
        <div style={{ fontWeight: 600 }}>{w.name || w.email}</div>
        <div style={{ display: 'flex', gap: 4, fontSize: 11, color: '#999' }}>
          <Pill label={w.gender || '—'} color={w.gender === 'Female' ? 'pink' : w.gender === 'Male' ? 'blue' : 'gray'} />
          <Pill label={`L${w.level || '?'}`} color='cream' />
          <span>· {new Date(w.createdAtUtc).toLocaleDateString()}</span>
        </div>
      </div>
    ))}
  </div>
);

// ----------------------------------------------------------------------------
// Courts grid: 5 (or session.courtCount) columns × 4 slots each.
// Drop a player on a slot → PATCH /pairings/{id}. If the slot is occupied,
// the backend swaps the two.
// ----------------------------------------------------------------------------
const CourtsBlock = ({ data, api, onChange }) => {
  if (!data.pairings) {
    return (
      <div style={{ background: '#fff', border: '1px dashed #ddd', borderRadius: 8, padding: 24, textAlign: 'center', color: '#666' }}>
        No pairings yet. Click <strong>↻ Generate pairings</strong> above to seed an initial layout, then drag to fine-tune.
      </div>
    );
  }
  const byCourt = {};
  for (let c = 1; c <= data.courtCount; c++) byCourt[c] = Array(4).fill(null);
  for (const p of data.pairings.placements) byCourt[p.courtIndex][p.slotIndex - 1] = p;

  const handleDrop = async (e, courtIndex, slotIndex) => {
    e.preventDefault();
    const regId = e.dataTransfer.getData('regId');
    if (!regId) return;
    // Find the existing pairing for this regId (if any) — that's the row to PATCH.
    const existing = data.pairings.placements.find((p) => p.registrationId === regId);
    if (!existing) {
      // Player came from the roster (not on a court yet). For v1 we only
      // re-arrange via PATCH; new placements would need a POST endpoint we
      // haven't built. Surface the limitation to the operator.
      alert('Not placed yet — generate pairings first, then drag.');
      return;
    }
    try {
      await api.playEvents.movePairing(existing.id, { courtIndex, slotIndex });
      await onChange();
    } catch (err) {
      alert(`Move failed: ${err?.message || 'unknown'}`);
    }
  };

  const findPlayer = (regId) => data.roster.find((r) => r.registrationId === regId);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
      {Object.keys(byCourt).map((c) => {
        const slots = byCourt[c];
        const placedPlayers = slots.filter(Boolean).map((p) => findPlayer(p.registrationId)).filter(Boolean);
        const meta = courtMeta(placedPlayers);
        return (
          <div key={c} style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ padding: '6px 10px', background: '#f5f5f5', borderBottom: '1px solid #eee', fontSize: 12, display: 'flex', justifyContent: 'space-between' }}>
              <strong>Court {c}</strong>
              <span style={{ color: '#666' }}>{slots.filter(Boolean).length}/4</span>
            </div>
            <div style={{ padding: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {slots.map((p, idx) => {
                const slotIndex = idx + 1;
                if (!p) {
                  return (
                    <div
                      key={`${c}-${slotIndex}`}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => handleDrop(e, parseInt(c, 10), slotIndex)}
                      style={{
                        border: '2px dashed #e0e0e0', borderRadius: 6, padding: '8px 6px',
                        fontSize: 11, color: '#999', textAlign: 'center',
                      }}
                    >
                      drop player
                    </div>
                  );
                }
                const player = findPlayer(p.registrationId);
                if (!player) return null;
                return (
                  <div
                    key={p.id}
                    draggable
                    onDragStart={(e) => e.dataTransfer.setData('regId', p.registrationId)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => handleDrop(e, parseInt(c, 10), slotIndex)}
                    style={{
                      border: '1px solid #e0e0e0', borderRadius: 6, padding: 6, cursor: 'grab',
                      background: p.manuallyEdited ? '#FFF6F1' : '#fafafa', fontSize: 12,
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>{player.name || player.email}</div>
                    <div style={{ display: 'flex', gap: 4, fontSize: 10, marginTop: 2 }}>
                      <Pill label={player.gender || '—'} color={player.gender === 'Female' ? 'pink' : player.gender === 'Male' ? 'blue' : 'gray'} />
                      <Pill label={`L${player.level || '?'}`} color='cream' />
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ padding: '6px 10px', borderTop: '1px solid #eee', fontSize: 10, color: '#666', textAlign: 'center' }}>
              {meta}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const PairingsInfo = ({ data }) => (
  <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 12, marginTop: 12, fontSize: 12, color: '#666' }}>
    {data.pairings ? (
      <>
        Generated {new Date(data.pairings.generatedAtUtc).toLocaleString()} · strategy: <strong>{data.pairings.strategy}</strong>
      </>
    ) : (
      <>No pairings generated yet for this session.</>
    )}
    <div style={{ marginTop: 4 }}>
      The pairings email is auto-scheduled 2h before kickoff. Late cancellations within the cutoff trigger a debounced re-pair + resend (capped at kickoff − 10 min).
    </div>
  </div>
);

const courtMeta = (players) => {
  if (players.length === 0) return 'Empty court';
  const genders = players.map((p) => p.gender).filter(Boolean);
  const levels = players.map((p) => p.level || 3);
  const allF = genders.every((g) => g === 'Female');
  const allM = genders.every((g) => g === 'Male');
  const min = Math.min(...levels), max = Math.max(...levels);
  const g = allF ? 'All women' : allM ? 'All men' : genders.length ? 'Mixed' : '—';
  return `${g} · Level ${min === max ? min : `${min}–${max}`}`;
};

const Pill = ({ label, color }) => {
  const colors = {
    pink:  { bg: '#FCE7F3', fg: '#9D174D' },
    blue:  { bg: '#DBEAFE', fg: '#1E3A8A' },
    gray:  { bg: '#E5E7EB', fg: '#374151' },
    cream: { bg: '#FAF7F2', fg: '#1a3559' },
  };
  const c = colors[color] || colors.gray;
  return (
    <span style={{
      background: c.bg, color: c.fg,
      fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
      borderRadius: 4, padding: '1px 6px',
    }}>{label}</span>
  );
};

PlayEventSessionInternal.propTypes = { api: PropTypes.object.isRequired };

export default connect(() => ({}))(withRouter(withApi(PlayEventSessionInternal)));
